interface PaymentItem {
	quantity?: number;
	price: number; // in dollars
}

export class Payment {
	private items: PaymentItem[];
	private discountDollars: number;

	constructor(items: PaymentItem[], discountDollars = 0) {
		this.items = items;
		this.discountDollars = discountDollars;
	}

	// always work in cents internally
	private subtotalCents(): number {
		return this.items
			.filter((item) => {
				return 'quantity' in item && Number(item.quantity) > 0;
			})
			.reduce((sum, item) => {
				const quantity = item.quantity ?? 1;
				return sum + quantity * Math.round(item.price * 100);
			}, 0);
	}

	private discountCents(): number {
		return Math.round(this.discountDollars * 100);
	}

	private discountedSubtotalCents(): number {
		const subtotal = this.subtotalCents();
		const discount = this.discountCents();
		return Math.max(subtotal - discount, 0);
	}

	private gstCents(): number {
		// GST is 10% of the discounted subtotal
		return Math.round(this.discountedSubtotalCents() * 0.1);
	}

	private totalCents(): number {
		// apply GST (10%) after discount
		return this.discountedSubtotalCents() + this.gstCents();
	}

	private totalWithoutGstCents(): number {
		return this.discountedSubtotalCents();
	}

	private depositCents(percent = 0.3): number {
		return Math.round(this.totalCents() * percent);
	}

	private depositWithoutGstCents(percent = 0.3): number {
		return Math.round(this.totalWithoutGstCents() * percent);
	}

	private balanceCents(percent = 0.3): number {
		return this.totalCents() - this.depositCents(percent);
	}

	subtotal(): number {
		return this.subtotalCents() / 100;
	}

	discount(): number {
		return this.discountCents() / 100;
	}

	discountedSubtotal(): number {
		return this.discountedSubtotalCents() / 100;
	}

	gst(): number {
		return this.gstCents() / 100;
	}

	total(): number {
		return this.totalCents() / 100;
	}

	totalWithoutGST(): number {
		return this.totalWithoutGstCents() / 100;
	}

	deposit(percent = 0.3): number {
		return this.depositCents(percent) / 100;
	}

	depositWithoutGST(percent = 0.3): number {
		return this.depositWithoutGstCents(percent) / 100;
	}

	balance(percent = 0.3): number {
		return this.balanceCents(percent) / 100;
	}
}
